/**
 * Resolve which party member's id becomes the new "active battler" after
 * sim emits |switch|p1a (or |drag|p1a — not in v0.1's roster but defensive).
 *
 * Player-initiated switches plumb `pendingSwitchToId` through: BattleScene
 * records it when sending `>p1 switch N` and consumes it here on the
 * matching incoming |switch|p1a. That's the canonical path and resolves
 * unambiguously for same-species duplicates (it carries the player's
 * choice of slot).
 *
 * Sim-INITIATED switches (Whirlwind / Roar in future rosters; sim-internal
 * forced switches that race our outgoing >p1 command on multi-event turns;
 * the first |switch|p1a at battle start before any player input) have no
 * pendingSwitchToId. Fallback: pick the first non-fainted team member of
 * the event's species. With two alive same-species, this picks the lower-
 * slot one — consistent with cartridge convention (sim picks slot order
 * on its own forced switches) and matches the pre-id BattleScene heuristic
 * for the path where the player wasn't choosing.
 *
 * Returns null when neither path resolves (empty team or all dead). The
 * caller logs and continues with the stale `activeId` — never crashes the
 * scene. Defensive path (k) of the Pokédex design doc.
 */

export interface TeamMemberRef {
  id: string;
  species: string;
}

export function resolveActiveId(
  team: ReadonlyArray<TeamMemberRef>,
  faintedIds: ReadonlySet<string>,
  eventSpecies: string,
  pendingSwitchToId: string | null,
): string | null {
  if (pendingSwitchToId && team.some((m) => m.id === pendingSwitchToId)) {
    return pendingSwitchToId;
  }
  const m = team.find((m) => m.species === eventSpecies && !faintedIds.has(m.id));
  return m?.id ?? null;
}
